import type {
  Money,
  ProductMediaReference,
  PublicCategorySummary,
  PublicProductDetail,
  PublicProductSummary,
} from '@emazy/shared-types';

import { presentationForCategory } from '@/features/catalog/data/categoryPresentation';

import type {
  CatalogMediaConfig,
  CategoryCardModel,
  ProductCardModel,
  ProductDetailViewModel,
} from './types';
import { CatalogError } from './types';

export function formatMoney(money: Money) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: money.currency,
  }).format(money.amountMinor / 100);
}

export function resolveCatalogMediaUrl(
  media: Pick<ProductMediaReference, 'objectKey'> | undefined,
  config: CatalogMediaConfig,
) {
  if (!media) return config.placeholderUrl;
  const segments = media.objectKey.split('/');
  if (
    !media.objectKey ||
    media.objectKey.startsWith('/') ||
    media.objectKey.includes('..') ||
    media.objectKey.includes('\\') ||
    media.objectKey.includes('://') ||
    [...media.objectKey].some((character) => {
      const codePoint = character.codePointAt(0) ?? 0;
      return codePoint < 32 || codePoint === 127;
    }) ||
    segments.some((segment) => !segment || segment === '.' || segment === '..')
  ) {
    throw new CatalogError('invalid-response', 'Catalog media reference is invalid.');
  }
  if (!config.baseUrl) return config.placeholderUrl;
  const encoded = segments.map(encodeURIComponent).join('/');
  return `${config.baseUrl.replace(/\/+$/, '')}/${encoded}`;
}

export function mapPublicCategoryToCategoryCardModel(
  category: PublicCategorySummary,
  media: CatalogMediaConfig,
): CategoryCardModel {
  const presentation = presentationForCategory(category.slug, category.name);
  return {
    id: category.id,
    slug: category.slug,
    title: category.name,
    shortTitle: presentation.shortTitle,
    description: category.shortDescription,
    route: presentation.route,
    icon: presentation.icon,
    ...(category.heroMedia
      ? {
          imageUrl: resolveCatalogMediaUrl(category.heroMedia, media),
          imageAlt: category.heroMedia.altText,
        }
      : {}),
  };
}

export function mapPublicProductSummaryToProductCardModel(
  product: PublicProductSummary,
  media: CatalogMediaConfig,
): ProductCardModel {
  return {
    id: product.id,
    slug: product.slug,
    category: product.categorySlug,
    name: product.name,
    shortDescription: product.shortDescription,
    startingPrice: product.startingPrice.amountMinor / 100,
    startingPriceLabel: `From ${formatMoney(product.startingPrice)}`,
    ...(product.compareAtPrice ? { compareAtPriceLabel: formatMoney(product.compareAtPrice) } : {}),
    currency: product.startingPrice.currency,
    imageUrl: resolveCatalogMediaUrl(product.primaryMedia, media),
    imageAlt: product.primaryMedia.altText,
    badges: [...product.badges],
    featured: product.featured,
    customizable: product.customizable,
    active: true,
  };
}

export function mapPublicProductDetailToProductDetailViewModel(
  product: PublicProductDetail,
  media: CatalogMediaConfig,
): ProductDetailViewModel {
  const summary = mapPublicProductSummaryToProductCardModel(product, media);
  const options = product.options
    .map((option) => ({
      ...option,
      values: option.values.filter((value) => value.active).sort(byDisplayOrder),
    }))
    .filter((option) => option.values.length > 0)
    .sort(byDisplayOrder);
  const customizationSummary = [
    ...product.customization.textInputs.map((input) => input.label),
    ...(product.customization.imageUpload ? ['Image or artwork upload'] : []),
    ...(product.customization.customerNotesAllowed ? ['Customer notes'] : []),
  ];
  return {
    ...summary,
    description: product.description,
    media: [...product.media].sort(byDisplayOrder).map((item) => ({
      id: item.id,
      url: resolveCatalogMediaUrl(item, media),
      alt: item.altText,
      ...(item.width ? { width: item.width } : {}),
      ...(item.height ? { height: item.height } : {}),
    })),
    options,
    customizationSummary,
    seo: {
      ...(product.seo.title ? { title: product.seo.title } : {}),
      ...(product.seo.description ? { description: product.seo.description } : {}),
    },
  };
}

function byDisplayOrder(first: { displayOrder: number }, second: { displayOrder: number }) {
  return first.displayOrder - second.displayOrder;
}
